/**
 * 读取官方 profile 映射；具体走哪条渠道由 official-channel-switch.js 决定。
 */
import { OFFICIAL_CHANNEL_SWITCH } from './official-channel-switch'

/** @param {string} v */
function normalizeChannel(v) {
  if (v === 'official' || v === 'pollo' || v === 'allall') return v
  return null
}

/**
 * @param {'video'|'image'|'music'} media
 * @returns {'pollo'|'official'|'allall'}
 */
export function resolveMediaDefaultChannel(media) {
  var sw = OFFICIAL_CHANNEL_SWITCH
  var g = normalizeChannel(sw.globalDefault) || 'pollo'
  var mediaKey =
    media === 'video' ? sw.videoDefault : media === 'image' ? sw.imageDefault : media === 'music' ? sw.musicDefault : null
  var v = normalizeChannel(mediaKey) || g
  return v || 'pollo'
}

/**
 * modelOverrides 按 video / image / music 分桶；
 * - Seedream：开关 key 迁移为 seedream-4（历史上曾用 seedream4，这里仍保留兼容逻辑）。
 */
function getModelOverrideRaw(sw, modelId, media) {
  var mo = sw.modelOverrides
  if (!mo) return undefined
  var bucket = media === 'video' ? mo.video : media === 'image' ? mo.image : media === 'music' ? mo.music : null
  if (!bucket || typeof bucket !== 'object') return undefined
  if (bucket[modelId] != null) return bucket[modelId]
  // 向后兼容：历史上 Seedream 的官方开关在 image bucket 里用键 seedream4
  if (modelId === 'seedream-4' && bucket.seedream4 != null) return bucket.seedream4
  // 向后兼容：Seedance 业务 modelId 可能仍为 seedance，但开关 key 已迁移到 seedance-1-5-pro
  if (modelId === 'seedance' && bucket['seedance-1-5-pro'] != null) return bucket['seedance-1-5-pro']
  return undefined
}

/**
 * 不可切换模型覆盖（优先级高于 modelOverrides）
 */
function getLockedModelOverrideRaw(sw, modelId, media) {
  var mo = sw.lockedModelOverrides
  if (!mo) return undefined
  var bucket = media === 'video' ? mo.video : media === 'image' ? mo.image : media === 'music' ? mo.music : null
  if (!bucket || typeof bucket !== 'object') return undefined
  if (bucket[modelId] != null) return bucket[modelId]
  return undefined
}

/**
 * @param {string} modelId
 * @param {'video'|'image'|'music'} media
 * @returns {'pollo'|'official'|'allall'}
 */
export function resolveModelChannel(modelId, media) {
  var sw = OFFICIAL_CHANNEL_SWITCH
  var rawLocked = getLockedModelOverrideRaw(sw, modelId, media)
  var locked = rawLocked != null ? normalizeChannel(String(rawLocked)) : null
  if (locked) return locked
  var raw = getModelOverrideRaw(sw, modelId, media)
  var override = raw != null ? normalizeChannel(String(raw)) : null
  if (override) return override
  return resolveMediaDefaultChannel(media)
}

/** 逻辑 modelId -> 官方视频 profile（仅当渠道为 official 且存在映射时使用） */
export var OFFICIAL_VIDEO_MODEL_PROFILE = {
  seedance: 'volc-seedance',
  'sora-2': 'volc-sora-2',
  'wan-2-2': 'dashscope-wan-2-2',
  'wan-2-5': 'dashscope-wan-2-5',
  'wan-2-6': 'dashscope-wan-2-6',
  'veo-3-0': 'google-veo-3',
  'veo-3-1': 'google-veo-3-1',
  'veo-3-1-fast': 'google-veo-3-1',
  'wan-animate': 'dashscope-wan-2-2-animate'
}

/** 官方图片 profile：Seedream 4 使用键 seedream4（与 modelOverrides 一致；业务 modelId 仍为 seedream-4） */
export var OFFICIAL_IMAGE_MODEL_PROFILE = {
  seedream4: 'volc-seedream-4',
  'seedream-5': 'volc-seedream-5',
  'gpt-image-1-5': 'volc-gpt-image-1-5',
  'qwen-image-2-0-pro': 'dashscope-qwen-image-2-0-pro',
  'z-image-turbo': 'dashscope-z-image-turbo',
  'nano-banana-2': 'google-nano-banana-2',
  'flux-pro-1-1-ultra': 'bfl-flux-pro-1-1-ultra',
  'flux-2': 'bfl-flux-2'
}

/** 逻辑 modelId -> 官方音乐 profile（预留） */
export var OFFICIAL_MUSIC_MODEL_PROFILE = {}

/**
 * @param {string} modelId
 * @returns {string|null} official video profile id，不走官方时返回 null
 */
export function getOfficialVideoProfile(modelId) {
  if (resolveModelChannel(modelId, 'video') !== 'official') return null
  var profile = OFFICIAL_VIDEO_MODEL_PROFILE[modelId]
  return profile || null
}

/**
 * @param {string} modelId
 * @returns {string|null}
 */
export function getOfficialImageProfile(modelId) {
  if (resolveModelChannel(modelId, 'image') !== 'official') return null
  var profile = OFFICIAL_IMAGE_MODEL_PROFILE[modelId]
  if (!profile && modelId === 'seedream-4') {
    profile = OFFICIAL_IMAGE_MODEL_PROFILE.seedream4
  }
  return profile || null
}

/**
 * @param {string} modelId
 * @returns {string|null}
 */
export function getOfficialMusicProfile(modelId) {
  if (resolveModelChannel(modelId, 'music') !== 'official') return null
  var profile = OFFICIAL_MUSIC_MODEL_PROFILE[modelId]
  return profile || null
}
